import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { LinearIcon } from "./linearIcon";
import { type LinearInitiativesPayload, parseLinearInitiativesPayload } from "./linearInitiativesSchema";
import { formatLinearDate, safeLinearUrl } from "./linearRenderUtils";

interface LinearInitiativesRendererProps {
  block: StreamingBlock;
}

export const LinearInitiativesRenderer = (props: LinearInitiativesRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LinearInitiativesPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLinearInitiativesPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("linear_initiatives_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().initiatives.length > 0}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <LinearIcon class="size-4 shrink-0 text-primary-text" />
                <span class="truncate text-sm font-medium text-primary-text">
                  {rt.t("linear_initiatives_card.title")}
                </span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">
                  {data().initiatives.length === 1
                    ? rt.t("linear_initiatives_card.results_one")
                    : rt.t("linear_initiatives_card.results", { count: String(data().initiatives.length) })}
                </span>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().initiatives}>
                  {(initiative) => (
                    <li class="px-3 py-2">
                      <div class="flex items-baseline justify-between gap-3">
                        <Show
                          when={safeLinearUrl(initiative.url)}
                          fallback={
                            <span class="truncate text-sm font-medium text-primary-text">{initiative.name}</span>
                          }
                        >
                          {(href) => (
                            <a
                              href={href()}
                              target="_blank"
                              rel="noopener noreferrer"
                              class="truncate text-sm font-medium text-brand hover:underline"
                            >
                              {initiative.name}
                            </a>
                          )}
                        </Show>
                        <Show when={initiative.targetDate}>
                          {(targetDate) => (
                            <span class="shrink-0 text-xs text-muted-text">{formatLinearDate(targetDate())}</span>
                          )}
                        </Show>
                      </div>
                      <div class="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
                        <span class="shrink-0 rounded bg-inset px-1.5 py-0.5">{initiative.status}</span>
                        <span class="shrink-0">
                          {initiative.projectCount === 1
                            ? rt.t("linear_initiatives_card.projects_one")
                            : rt.t("linear_initiatives_card.projects", { count: String(initiative.projectCount) })}
                        </span>
                      </div>
                      <Show when={initiative.description}>
                        {(description) => (
                          <p class="mt-1 line-clamp-2 text-sm wrap-break-word text-primary-text">{description()}</p>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
